/**
 * HIT SPARKS — the "your attack LANDED" burst (user call 2026-07-23: landed hits need an
 * obvious audio+visual cue; this is the visual half — the positional hit sfx + hitstop +
 * the victim's white flash ride alongside).
 *
 * A pooled set of additive radial-glow sprites: each confirmed hit pops one at the contact
 * point — fast scale-out + fade over ~0.2 s, slight upward drift. World-space storage with
 * the floating-origin pattern (same as dmgNums/bladeTrail): re-based against `origin` every
 * update, so nothing smears as the world carries.
 */
import * as THREE from "three";

const POOL = 12;
const LIFE = 0.22;

function sparkTexture(): THREE.CanvasTexture {
  const c = document.createElement("canvas");
  c.width = c.height = 64;
  const g = c.getContext("2d")!;
  const grad = g.createRadialGradient(32, 32, 0, 32, 32, 32);
  grad.addColorStop(0, "rgba(255,255,255,1)"); // hot core
  grad.addColorStop(0.18, "rgba(255,244,214,0.95)");
  grad.addColorStop(0.45, "rgba(255,196,120,0.35)"); // impact-orange falloff
  grad.addColorStop(1, "rgba(255,180,90,0)");
  g.fillStyle = grad;
  g.fillRect(0, 0, 64, 64);
  // Four thin star spokes over the glow — reads as a strike, not a lamp.
  g.globalCompositeOperation = "lighter";
  g.strokeStyle = "rgba(255,255,255,0.9)";
  g.lineWidth = 2;
  for (const [dx, dy] of [[1, 0], [0, 1], [0.7, 0.7], [0.7, -0.7]]) {
    g.beginPath();
    g.moveTo(32 - dx * 30, 32 - dy * 30);
    g.lineTo(32 + dx * 30, 32 + dy * 30);
    g.stroke();
  }
  return new THREE.CanvasTexture(c);
}

interface Spark {
  sprite: THREE.Sprite;
  world: THREE.Vector3;
  age: number;
  size: number;
}

export class HitSparks {
  readonly group = new THREE.Group();
  private readonly pool: Spark[] = [];
  private next = 0;

  constructor() {
    const map = sparkTexture();
    for (let i = 0; i < POOL; i++) {
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({
        map,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        transparent: true,
        rotation: Math.PI * 2 * (i / POOL), // varied spoke angles across the pool
      }));
      sprite.visible = false;
      this.group.add(sprite);
      this.pool.push({ sprite, world: new THREE.Vector3(), age: LIFE, size: 1 });
    }
    this.group.renderOrder = 6; // over the world + the blade streak
  }

  /** Pop a burst at the WORLD-space contact point. `heavy` bursts bigger. */
  spawn(pos: THREE.Vector3, heavy = false): void {
    const s = this.pool[this.next];
    this.next = (this.next + 1) % POOL;
    s.world.copy(pos);
    s.age = 0;
    s.size = heavy ? 1.9 : 1.25;
    s.sprite.material.rotation = Math.random() * Math.PI * 2;
    s.sprite.visible = true;
  }

  update(dt: number, origin: THREE.Vector3): void {
    for (const s of this.pool) {
      if (!s.sprite.visible) continue;
      s.age += dt;
      if (s.age >= LIFE) {
        s.sprite.visible = false;
        continue;
      }
      const t = s.age / LIFE;
      s.world.y += dt * 0.4; // slight lift as it dies
      s.sprite.position.copy(s.world).sub(origin);
      s.sprite.scale.setScalar(s.size * (0.35 + 1.25 * Math.sqrt(t))); // fast pop-out
      s.sprite.material.opacity = Math.pow(1 - t, 1.4);
    }
  }
}
